<script>
/**
 * Seletor da voz da IA no Modo de Voz (spec-016, [US-1]/[US-3]).
 *
 * Componente BURRO: recebe o catálogo já carregado (`vozes`, do servidor —
 * "o front NÃO tem constante própria", spec.md §2) e não chama `apiVoz`
 * nem despacha no store — mesma disciplina de `agent-select`/`escopo-select`
 * (montável sem sessão no teste). O botão de "ouvir" (US-3) emite `ouvir`
 * e devolve ao PAI o estado de carregamento/erro daquela amostra
 * (`amostra-tocando-id`/`amostra-erro`) — tocar áudio e chamar
 * `apiVoz.amostra` são responsabilidade de quem monta este componente
 * (`send-message.vue`), não deste componente.
 *
 * Não usa `ComposerInlineSelect` (ao contrário de `agent-select`): cada
 * linha do menu precisa de DOIS controles independentes — escolher a voz e
 * ouvir a amostra dela — e o `ComposerInlineSelect` só aceita um `onClick`
 * por opção (a linha inteira é um `<button>` só). Dois `<button>` irmãos
 * dentro de um `<span>`, como `escopo-select.vue` já faz para abrir/remover.
 */
import { COMPOSER_CHAT } from '@modules/aawz-intelligence-v2/constants/TextChat'

const TAMANHO_ICONE = 13

export default {
  name: 'VozSelect',
  props: {
    // Já FILTRADO/carregado pelo pai (`apiVoz.catalogo()`) — este componente
    // não lê API nem store, para continuar puro e testável sem sessão.
    vozes: {
      type: Array,
      default: () => [],
    },
    // `''` = nenhuma escolhida (US-1 AC2) — o chip mostra o rótulo genérico.
    vozAtivaId: {
      type: String,
      default: '',
    },
    // Só o ícone, sem rótulo — o composer do modo limpo (mesma prop dos
    // chips irmãos: agente, modelo, escopo).
    soIcone: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    // Id da voz cuja amostra está sendo buscada/tocando agora, ou `''`.
    // Desabilita SÓ o botão de ouvir daquela linha — escolher outra voz
    // continua livre enquanto uma amostra toca.
    amostraTocandoId: {
      type: String,
      default: '',
    },
    // `{vozId, mensagem} | null` — US-3 AC4: a falha da amostra mostra o
    // MOTIVO, nunca um botão que não faz nada.
    amostraErro: {
      type: Object,
      default: null,
    },
  },
  data() {
    return { aberto: false, TAMANHO_ICONE }
  },
  computed: {
    vozAtiva() {
      return this.vozes.find((voz) => voz.id === this.vozAtivaId) || null
    },
    rotuloChip() {
      return this.vozAtiva ? this.vozAtiva.rotulo : COMPOSER_CHAT.vozRotulo
    },
    tooltipDoChip() {
      return COMPOSER_CHAT.vozTooltip + ': ' + this.rotuloChip
    },
    // Sem voz nenhuma no catálogo (carga ainda não chegou, ou falhou), o
    // chip não tem o que oferecer — mesma régua do `agent-select` (`temAgente`).
    temVozes() {
      return this.vozes.length > 0
    },
    // Rótulo de "ouvir" e erro da amostra pré-calculados por linha — no
    // template, dentro do `v-for`, seriam N chamadas a cada re-render
    // (`amostraTocandoId`/`amostraErro` mudam a cada amostra).
    linhas() {
      return this.vozes.map((voz) => ({
        ...voz,
        rotuloOuvir: this.rotuloDeOuvir(voz),
        erro: this.erroDaVoz(voz),
      }))
    },
  },
  methods: {
    escolher(voz) {
      this.aberto = false
      this.$emit('change', voz.id)
    },
    // `.stop` — clicar em "ouvir" não pode também escolher a voz (as duas
    // ações são irmãs, não uma dispara a outra) nem fechar o menu: comparar
    // as quatro amostras (o golden da spec) exige o menu aberto entre elas.
    ouvir(voz, evento) {
      if (evento) evento.stopPropagation()
      this.$emit('ouvir', voz.id)
    },
    erroDaVoz(voz) {
      if (!this.amostraErro || this.amostraErro.vozId !== voz.id) return ''
      return this.amostraErro.mensagem || COMPOSER_CHAT.vozAmostraFalha
    },
    rotuloDeOuvir(voz) {
      return COMPOSER_CHAT.vozOuvirAmostra(voz.rotulo)
    },
  },
}
</script>

<template>
  <v-menu
    v-if="temVozes"
    v-model="aberto"
    :close-on-content-click="false"
    offset-y
    top
    content-class="voz-select__menu"
  >
    <!-- Só o `on` do v-menu no gatilho: ele já alterna abrir/fechar, e um
         `@click` que forçasse `aberto = true` reabria o menu no clique que
         devia fechá-lo. `disabled` no botão basta — sem clique, sem `on`. -->
    <template v-slot:activator="{ on, attrs }">
      <button
        v-bind="attrs"
        type="button"
        class="voz-select__gatilho ia-texto-auxiliar"
        :class="{ 'voz-select__gatilho--so-icone': soIcone }"
        :disabled="disabled"
        :aria-label="soIcone ? tooltipDoChip : null"
        :title="soIcone ? tooltipDoChip : null"
        v-on="on"
      >
        <icon-svg name="mdi-volume-high" :size="TAMANHO_ICONE" color="var(--primary)" />
        <span v-if="!soIcone" class="text-truncate">{{ rotuloChip }}</span>
        <icon-svg
          v-if="!soIcone"
          name="mdi-chevron-down"
          :size="10"
          color="var(--neutral-400)"
        />
      </button>
    </template>

    <div class="voz-select__painel">
      <div v-for="voz in linhas" :key="voz.id" class="voz-select__linha">
        <button
          type="button"
          class="voz-select__opcao ia-texto-corpo"
          :class="{ 'voz-select__opcao--ativa': voz.id === vozAtivaId }"
          @click="escolher(voz)"
        >
          <icon-svg
            v-if="voz.id === vozAtivaId"
            name="mdi-check"
            :size="TAMANHO_ICONE"
            color="var(--primary)"
          />
          <span class="text-truncate">{{ voz.rotulo }}</span>
        </button>

        <button
          type="button"
          class="voz-select__ouvir"
          :disabled="amostraTocandoId === voz.id"
          :aria-label="voz.rotuloOuvir"
          :title="voz.rotuloOuvir"
          @click="ouvir(voz, $event)"
        >
          <v-progress-circular
            v-if="amostraTocandoId === voz.id"
            indeterminate
            size="12"
            width="2"
            color="var(--primary)"
          />
          <icon-svg v-else name="mdi-play" :size="12" color="var(--neutral-600)" />
        </button>

        <p v-if="voz.erro" class="voz-select__erro ia-texto-auxiliar" role="alert">
          {{ voz.erro }}
        </p>
      </div>
    </div>
  </v-menu>
</template>

<style lang="sass" scoped>
// Mesma receita dos três chips irmãos do composer (agente, modelo, escopo):
// preenchimento neutro, cor no ícone. Mudou aqui, mude nos outros três
// (`grep -rn "chip do composer"`).
.voz-select__gatilho
  color: var(--neutral-900)
  display: inline-flex
  align-items: center
  gap: 4px
  border-radius: 999px
  padding: 4px 10px
  background: var(--neutral-100)

  &:hover
    background: var(--neutral-200)

  &:disabled
    opacity: 0.5
    cursor: not-allowed

  &--so-icone
    width: 34px
    height: 34px
    padding: 0
    justify-content: center
    border-radius: 50%
    background: transparent

.voz-select__painel
  width: 220px
  padding: 4px

.voz-select__linha
  display: flex
  // `wrap` é o que deixa o `__erro` (flex-basis 100%) cair para a linha de
  // baixo em vez de espremer os dois botões.
  flex-wrap: wrap
  align-items: center
  gap: 4px

.voz-select__opcao
  color: var(--neutral-900)
  flex: 1
  display: inline-flex
  align-items: center
  gap: 6px
  min-width: 0
  padding: 6px 8px
  border-radius: 6px
  text-align: left

  &:hover
    background: var(--neutral-100)

  &--ativa
    color: var(--primary)
    font-weight: 500

.voz-select__ouvir
  flex-shrink: 0
  display: inline-flex
  align-items: center
  justify-content: center
  width: 24px
  height: 24px
  border-radius: 50%

  &:hover
    background: var(--neutral-100)

  &:disabled
    cursor: not-allowed

.voz-select__erro
  color: var(--danger-text)
  flex-basis: 100%
  margin: 0 0 4px 8px
</style>
